import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PermissionsGrid } from "@/components/permissions-grid";
import { useAbility } from "@/hooks/use-ability";
import { useMyAbilities } from "@/hooks/use-abilities";
import { useCurrentUser } from "@/hooks/use-current-user";
import { ApiError } from "@/lib/api-client";

// ADV-896: diagnostic page for the CASL tracer-bullet. Renders the same
// permissions grid as the settings panel plus a side-by-side dump of the
// client-derived rules (from <AbilityProvider>) and the server-derived rules
// (from GET /auth/me/abilities). Lets us eyeball drift between the two
// derivations before Phase 2/3 migrates ~84 call sites.

export default function V2DevAbilitiesPage() {
  const currentUser = useCurrentUser();
  const ability = useAbility();
  const { data, error, isLoading, isFetching, refetch } = useMyAbilities();

  const scope = useMemo(
    () => ({
      accountId: currentUser?.accountId ?? "",
      businessId: currentUser?.businessId || null,
      userId: currentUser?.userId ?? null,
    }),
    [currentUser],
  );

  const clientRulesJson = useMemo(
    () => JSON.stringify(ability.rules, null, 2),
    [ability],
  );

  const serverRulesJson = useMemo(
    () => (data ? JSON.stringify(data.rules, null, 2) : null),
    [data],
  );

  // The endpoint is gated by requireAbility("read", "User") — admin and
  // super_admin only. Surface the 403 explicitly rather than masking it; the
  // client-side grid still works for everyone via <AbilityProvider>. Detect
  // via status code (not message text) because the server returns
  // "Insufficient permissions: cannot read User", which never contains
  // "forbidden".
  const forbidden = error instanceof ApiError && error.status === 403;

  return (
    <div className="min-h-svh bg-bg p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="space-y-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 typo-body text-text-lo hover:text-text-hi"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to app
          </Link>
          <h1 className="font-display text-title font-light text-text-hi">
            Abilities (CASL)
          </h1>
          <p className="typo-body text-text-md max-w-3xl">
            Diagnostic view of the CASL ability model from ADV-879. Use this to
            confirm the client&apos;s <code>defineAbilityFor</code> output
            matches what the server returns from{" "}
            <code className="typo-mono text-accent">/auth/me/abilities</code>.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Your roles</CardTitle>
            <CardDescription>
              Roles come from JWT <code>app_metadata</code> via the auth
              middleware mapper (see{" "}
              <code className="typo-mono">middleware/auth.ts</code>).
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Badge variant="outline">
              system: {currentUser?.systemRole ?? "—"}
            </Badge>
            <Badge variant="outline">
              end-user: {currentUser?.endUserRole ?? "—"}
            </Badge>
            <Badge variant="outline">
              account: {currentUser?.accountId ?? "—"}
            </Badge>
            <Badge variant="outline">
              business: {currentUser?.businessId || "—"}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permissions grid (client-derived)</CardTitle>
            <CardDescription>
              Rendered via <code>useAbility().can()</code> — always works,
              regardless of the server&apos;s gate on /auth/me/abilities.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PermissionsGrid ability={ability} scope={scope} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Raw rules — client vs server</CardTitle>
              <CardDescription>
                <code>ability.rules</code> from{" "}
                <code className="typo-mono">@adva/auth</code> on the left,{" "}
                <code className="typo-mono">/auth/me/abilities</code> response
                on the right. Drift here means the JWT mapper and{" "}
                <code>defineAbilityFor</code> disagree.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
              />
              Refetch server
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {forbidden ? (
              <Alert>
                <AlertTitle>Server endpoint denied (403)</AlertTitle>
                <AlertDescription>
                  <code className="typo-mono">/auth/me/abilities</code> is gated
                  by{" "}
                  <code className="typo-mono">
                    requireAbility(&quot;read&quot;, &quot;User&quot;)
                  </code>
                  . Only <strong>admin</strong> and <strong>super_admin</strong>{" "}
                  pass — the deny path is the tracer-bullet confirming the new
                  middleware actually runs (ADV-879 PR #633). The client-side
                  grid above is unaffected.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="typo-label text-text-md mb-2">
                  Client (defineAbilityFor → useAbility)
                </p>
                <pre className="typo-mono text-xs bg-elevated rounded-md p-3 overflow-auto max-h-[60vh]">
                  {clientRulesJson}
                </pre>
              </div>
              <div>
                <p className="typo-label text-text-md mb-2">
                  Server (/auth/me/abilities)
                </p>
                <pre className="typo-mono text-xs bg-elevated rounded-md p-3 overflow-auto max-h-[60vh]">
                  {isLoading
                    ? "Loading…"
                    : forbidden
                      ? "(server response unavailable — see banner above)"
                      : error
                        ? `Error: ${error instanceof Error ? error.message : String(error)}`
                        : (serverRulesJson ?? "—")}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
